import { createAuthClient } from 'better-auth/react';

import { capacitorAuthPlugin } from './capacitor-auth-plugin';
import { withCapacitorAuthFetch } from './capacitor-fetch';
import { withDevAuthFetch } from './dev-impersonation';
import { isNativePlatform } from './is-native';
import { getApiBaseUrl } from '../env';
import { reportNetworkError } from '../net/net-status';

const native = isNativePlatform();

function readAuthTimeoutMs(): number {
  // import.meta.env is undefined under the node unit-test runner (no Vite).
  const raw = import.meta.env?.VITE_AUTH_TIMEOUT_MS as string | undefined;
  const parsed = raw ? Number.parseInt(raw, 10) : NaN;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 45000;
}

const AUTH_REQUEST_TIMEOUT_MS = readAuthTimeoutMs();

function requestUrl(input: Parameters<typeof fetch>[0]): string {
  if (typeof input === 'string') {
    return input;
  }
  if (input instanceof URL) {
    return input.toString();
  }
  return (input as Request).url ?? 'unknown';
}

function withTimeoutLogging(impl: typeof fetch): typeof fetch {
  return (async (input, init) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), AUTH_REQUEST_TIMEOUT_MS);
    if (init?.signal) {
      if (init.signal.aborted) {
        controller.abort();
      } else {
        init.signal.addEventListener('abort', () => controller.abort(), { once: true });
      }
    }
    const start = Date.now();
    const url = requestUrl(input);
    try {
      const res = await impl(input, { ...init, signal: controller.signal });
      console.log('[CS-NET] auth', url, res.status, `${Date.now() - start}ms`);
      return res;
    } catch (err) {
      const aborted = controller.signal.aborted;
      const detail = aborted
        ? `timeout after ${AUTH_REQUEST_TIMEOUT_MS}ms`
        : err instanceof Error
          ? err.message
          : String(err);
      console.error('[CS-NET] auth failed', url, `${Date.now() - start}ms`, detail);
      // Only surface when our own timeout fired; better-auth aborts in-flight
      // session refreshes routinely and those should not alarm the user.
      if (aborted && !init?.signal?.aborted) {
        reportNetworkError({
          message: 'Signing in is taking longer than expected. Your connection may be unstable.',
          detail: `auth ${url} (${detail})`,
        });
      }
      throw err;
    } finally {
      clearTimeout(timeout);
    }
  }) as typeof fetch;
}

function createFetchImpl(): typeof fetch {
  const base = withDevAuthFetch(fetch);
  const withAuth = native ? withCapacitorAuthFetch(base) : base;
  return withTimeoutLogging(withAuth);
}

const webAuthClient = createAuthClient({
  baseURL: getApiBaseUrl(),
  fetchOptions: {
    credentials: 'include',
    customFetchImpl: createFetchImpl(),
  },
});

const nativeAuthClient = createAuthClient({
  baseURL: getApiBaseUrl(),
  plugins: [capacitorAuthPlugin()],
  disableDefaultFetchPlugins: true,
  fetchOptions: {
    credentials: 'omit',
    customFetchImpl: createFetchImpl(),
  },
});

export const authClient = native ? nativeAuthClient : webAuthClient;

export const { useSession, signIn, signOut } = authClient;
